/**
 * routeSuggest.ts — "Worth a stop on the way": a few photo cards for places
 * near the middle of each drive, each one insertable at exactly that leg.
 *
 * Data comes from the same bounds search the map uses while you pan (cheap,
 * cached server-side), one small box per leg. This file is the pure part:
 * where to look, and which listings make good cards.
 */

import type { Entity } from '../../types';
import { getOptimizedEntityImage } from '../../utils/entityImage';
import { DEFAULT_FALLBACK_IMAGE } from '../../utils/normalizeImageSrc';

export interface LegPoint {
  name: string;
  lat?: number;
  lng?: number;
  entityId?: number;
}

export interface RouteSuggestion {
  key: string;
  entityId: number;
  name: string;
  /** "Caravan park · Cann River" */
  detail: string;
  image: string;
  lat: number;
  lng: number;
  /** The new stop goes at this index (between legs[legIndex] and the next). */
  insertAt: number;
  fromName: string;
  toName: string;
}

export interface Box {
  north: number;
  south: number;
  east: number;
  west: number;
}

const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** Legs long enough to need a break: under this, there's no "on the way". */
export const MIN_LEG_KM = 120;

function km(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/** Legs at least this long get two looks (a third and two thirds along). */
export const SPLIT_LEG_KM = 400;

/**
 * Search boxes (about 45 km each way) along the legs worth breaking up: the
 * middle of a medium leg, a third and two thirds along a long one. Longest
 * legs first, at most `max` boxes, returned in trip order.
 */
export function legBoxes(
  stops: LegPoint[],
  max = 4,
  /** Where fraction `t` of leg `i` actually is on the road, when the road
   *  line is known; the straight line between the stops otherwise. */
  pointAt?: (legIndex: number, t: number) => { lat: number; lng: number } | null
): { insertAt: number; box: Box; from: LegPoint; to: LegPoint }[] {
  const legs: { i: number; a: LegPoint & { lat: number; lng: number }; b: LegPoint & { lat: number; lng: number }; len: number }[] = [];
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i];
    const b = stops[i + 1];
    if (!finite(a.lat) || !finite(a.lng) || !finite(b.lat) || !finite(b.lng)) continue;
    const len = km({ lat: a.lat, lng: a.lng }, { lat: b.lat, lng: b.lng });
    if (len >= MIN_LEG_KM) legs.push({ i, a: a as LegPoint & { lat: number; lng: number }, b: b as LegPoint & { lat: number; lng: number }, len });
  }
  legs.sort((x, y) => y.len - x.len);
  const out: { insertAt: number; box: Box; from: LegPoint; to: LegPoint; t: number }[] = [];
  for (const leg of legs) {
    const fractions = leg.len >= SPLIT_LEG_KM ? [1 / 3, 2 / 3] : [1 / 2];
    for (const t of fractions) {
      if (out.length >= max) break;
      const onRoad = pointAt?.(leg.i, t);
      const lat = onRoad ? onRoad.lat : leg.a.lat + (leg.b.lat - leg.a.lat) * t;
      const lng = onRoad ? onRoad.lng : leg.a.lng + (leg.b.lng - leg.a.lng) * t;
      const dLat = 0.4;
      const dLng = 0.4 / Math.max(0.3, Math.cos((lat * Math.PI) / 180));
      out.push({
        insertAt: leg.i + 1,
        box: { north: lat + dLat, south: lat - dLat, east: lng + dLng, west: lng - dLng },
        from: leg.a,
        to: leg.b,
        t,
      });
    }
  }
  return out
    .sort((x, y) => x.insertAt - y.insertAt || x.t - y.t)
    .map(({ t: _t, ...rest }) => rest);
}

const TYPE_LABEL: Record<string, string> = {
  caravan_park: 'Caravan park',
  campground: 'Campground',
  free_camping: 'Free camp',
  // Same words as the camp cards everywhere else: what the place IS.
  accom: 'Cabins & motels',
  accommodation: 'Cabins & motels',
  day_rest_area: 'Rest area',
  tours: 'Things to do',
};

/** What makes a good card: a real photo first, then a place to sleep. */
const TYPE_SCORE: Record<string, number> = {
  caravan_park: 3,
  campground: 3,
  free_camping: 2,
  day_rest_area: 1,
  accom: 1,
  accommodation: 1,
};

function entityPoint(e: Entity): { lat: number; lng: number } | null {
  const a = (e.addresses ?? [])[0] as { gps_latitude?: string | number; gps_longitude?: string | number } | undefined;
  const lat = Number(a?.gps_latitude);
  const lng = Number(a?.gps_longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return null;
  return { lat, lng };
}

/**
 * Booking-feed photos (Bookeasy) of caravan parks and holiday parks are
 * usually a cabin interior: a bedroom under "Caravan park" reads as a wrong
 * picture. Those cards get the drawn tile for their kind instead.
 */
// Bookeasy CDN links, and the booking-feed photos harvested onto our own
// media host (/media/park-images/<id>/...), which are mostly cabin rooms.
const BOOKING_FEED_PHOTO = /bookeasy|\/media\/park-images\//i;

export function untrustedPlacePhoto(type: string, ...urls: (string | null | undefined)[]): boolean {
  const roomy = /caravan|holiday|park/i.test(type) && !/national|rest/i.test(type);
  return roomy && urls.some((u) => typeof u === 'string' && BOOKING_FEED_PHOTO.test(u));
}

/**
 * Best card for one leg from a bounds-search result: must have a real photo
 * and a point, mustn't already be on the trip. Ties go to places you can
 * sleep, then to the one nearest the leg's middle.
 */
export function pickForLeg(
  entities: Entity[],
  leg: { insertAt: number; box: Box; from: LegPoint; to: LegPoint },
  onTrip: Set<number>,
  taken: Set<number>,
  /** Optional extra test on a candidate's point (e.g. close to the road). */
  keep?: (pt: { lat: number; lng: number }) => boolean
): RouteSuggestion | null {
  const mid = { lat: (leg.box.north + leg.box.south) / 2, lng: (leg.box.east + leg.box.west) / 2 };
  let best: { s: RouteSuggestion; score: number } | null = null;
  for (const e of entities) {
    const id = Number(e.id ?? (e as unknown as { entity_id?: number }).entity_id);
    if (!Number.isFinite(id) || onTrip.has(id) || taken.has(id)) continue;
    const pt = entityPoint(e);
    if (!pt || (keep && !keep(pt))) continue;
    const image = getOptimizedEntityImage(e, { width: 320, quality: 70 });
    if (!image || image === DEFAULT_FALLBACK_IMAGE || /placeholder|default/i.test(image)) continue;
    const type = String(e.entity_type ?? '');
    const town = String(((e.addresses ?? [])[0] as { town?: string } | undefined)?.town ?? '').trim();
    const raw = e as unknown as { image_src?: string; hero_image?: string; images?: unknown };
    const untrusted = untrustedPlacePhoto(type, image, raw.image_src, raw.hero_image);
    // A trustworthy photo wins a tie; an untrusted one still offers the
    // place, drawn.
    const score = (TYPE_SCORE[type] ?? 0) * 100 - km(pt, mid) - (untrusted ? 25 : 0);
    if (best && score <= best.score) continue;
    best = {
      score,
      s: {
        key: `route-sugg:${id}`,
        entityId: id,
        name: String(e.name ?? '').trim() || 'Unnamed place',
        detail: [TYPE_LABEL[type] ?? 'Place', town].filter(Boolean).join(' · '),
        image: untrusted ? '' : image,
        lat: pt.lat,
        lng: pt.lng,
        insertAt: leg.insertAt,
        fromName: leg.from.name,
        toName: leg.to.name,
      },
    };
  }
  return best?.s ?? null;
}

/**
 * Search boxes along ONE leg (a quarter, half and three quarters along), for
 * the ideas an open "add a stop here" box shows before you type.
 */
export function boxesForLeg(
  a: LegPoint,
  b: LegPoint,
  insertAt: number,
  pointAt?: (t: number) => { lat: number; lng: number } | null
): { insertAt: number; box: Box; from: LegPoint; to: LegPoint }[] {
  if (!finite(a.lat) || !finite(a.lng) || !finite(b.lat) || !finite(b.lng)) return [];
  const len = km({ lat: a.lat, lng: a.lng }, { lat: b.lat, lng: b.lng });
  if (len < 30) return [];
  const fractions = len >= 250 ? [0.25, 0.5, 0.75] : [0.5];
  return fractions.map((t) => {
    const onRoad = pointAt?.(t);
    const lat = onRoad ? onRoad.lat : (a.lat as number) + ((b.lat as number) - (a.lat as number)) * t;
    const lng = onRoad ? onRoad.lng : (a.lng as number) + ((b.lng as number) - (a.lng as number)) * t;
    // Short legs look in a tighter box, so ideas stay between the two stops.
    const dLat = Math.min(0.4, Math.max(0.12, len / 600));
    const dLng = dLat / Math.max(0.3, Math.cos((lat * Math.PI) / 180));
    return { insertAt, box: { north: lat + dLat, south: lat - dLat, east: lng + dLng, west: lng - dLng }, from: a, to: b };
  });
}

/**
 * Up to `max` ideas for one leg, in driving order: the best card from each
 * box first, then seconds from the boxes in turn until there are enough.
 */
export function ideasForLeg(
  resultsPerBox: Entity[][],
  boxes: { insertAt: number; box: Box; from: LegPoint; to: LegPoint }[],
  onTrip: Set<number>,
  max = 4,
  keep?: (pt: { lat: number; lng: number }) => boolean
): RouteSuggestion[] {
  const taken = new Set<number>();
  const perBox: RouteSuggestion[][] = boxes.map(() => []);
  for (let round = 0; round < max; round++) {
    let added = false;
    for (let i = 0; i < boxes.length; i++) {
      const total = perBox.reduce((n, l) => n + l.length, 0);
      if (total >= max) break;
      const card = pickForLeg(resultsPerBox[i] ?? [], boxes[i], onTrip, taken, keep);
      if (card) {
        taken.add(card.entityId);
        perBox[i].push(card);
        added = true;
      }
    }
    if (!added) break;
  }
  return perBox.flat();
}
